import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';

type Cmd = { id: string; label: string; path: string; hint: string; k: string };

const COMMANDS: Cmd[] = [
  { id: 'dash', label: 'Dashboard', path: '/dashboard', hint: 'Home overview', k: 'dashboard' },
  { id: 'leads', label: 'Leads CRM', path: '/leads', hint: 'Pipeline & WhatsApp', k: 'leads' },
  { id: 'agents', label: 'AI Agents', path: '/agents', hint: 'Fleet toggles', k: 'agents' },
  { id: 'content', label: 'AI Content Generator', path: '/content-generator', hint: 'Posts & captions', k: 'content' },
  { id: 'ops', label: 'Agent Operations', path: '/agent-ops', hint: 'Lead score & tools', k: 'ops' },
  { id: 'analytics', label: 'Analytics', path: '/analytics', hint: 'Performance', k: 'analytics' },
  { id: 'calendar', label: 'Content Calendar', path: '/calendar', hint: 'Schedule', k: 'calendar' },
  { id: 'calls', label: 'AI Calls', path: '/calls', hint: 'Dialer', k: 'calls' },
  { id: 'settings', label: 'Settings', path: '/settings', hint: 'Profile & billing', k: 'settings' },
  { id: 'support', label: 'Support', path: '/support', hint: 'Tickets', k: 'support' },
];

type PaletteCtx = { openPalette: () => void };
const Ctx = createContext<PaletteCtx>({ openPalette: () => {} });

function CommandPaletteModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return COMMANDS;
    return COMMANDS.filter(
      (c) =>
        c.label.toLowerCase().includes(s) ||
        c.hint.toLowerCase().includes(s) ||
        c.k.includes(s) ||
        c.path.includes(s)
    );
  }, [q]);

  useEffect(() => {
    if (open) {
      setQ('');
      setHi(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [open, onClose]);

  useEffect(() => {
    setHi(0);
  }, [q]);

  if (!open) return null;

  function go(c: Cmd) {
    nav(c.path);
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center bg-black/65 p-4 pt-[12vh] backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      onClick={onClose}
    >
      <div
        className="odc-card w-full max-w-lg !p-0 shadow-2xl"
        style={{ transform: 'none', borderColor: 'rgba(190,81,3,0.35)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b px-3 py-2" style={{ borderColor: 'var(--border)' }}>
          <Search className="h-4 w-4 shrink-0 opacity-60" />
          <input
            ref={inputRef}
            className="min-w-0 flex-1 border-0 bg-transparent py-2 text-sm outline-none"
            style={{ color: 'var(--text)' }}
            placeholder="Jump to page… (try “lead”, “agent”, “settings”)"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHi((i) => Math.min(filtered.length - 1, i + 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHi((i) => Math.max(0, i - 1));
              } else if (e.key === 'Enter' && filtered[hi]) {
                e.preventDefault();
                go(filtered[hi]);
              }
            }}
          />
          <kbd className="hidden shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-bold sm:inline" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}>
            esc
          </kbd>
        </div>
        <ul className="max-h-[min(60vh,420px)] overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <li className="px-4 py-8 text-center text-sm" style={{ color: 'var(--muted)' }}>
              Koi match nahi mila — alag keyword try karein.
            </li>
          ) : (
            filtered.map((c, i) => {
              const active = i === hi;
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    className="flex w-full items-start gap-3 px-4 py-3 text-left text-sm transition"
                    style={{
                      background: active ? 'rgba(190,81,3,0.14)' : undefined,
                      borderLeft: active ? '3px solid #BE5103' : '3px solid transparent',
                    }}
                    onMouseEnter={() => setHi(i)}
                    onClick={() => go(c)}
                  >
                    <span className="font-bold" style={{ color: 'var(--text)' }}>
                      {c.label}
                    </span>
                    <span className="ml-auto text-xs" style={{ color: 'var(--muted)' }}>
                      {c.hint}
                    </span>
                  </button>
                </li>
              );
            })
          )}
        </ul>
        <p className="border-t px-4 py-2 text-[10px]" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}>
          ↑↓ select · Enter open · ⌘K / Ctrl+K anywhere in app (when logged in)
        </p>
      </div>
    </div>
  );
}

export function CommandPaletteProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);

  const openPalette = useCallback(() => setOpen(true), []);
  const closePalette = useCallback(() => setOpen(false), []);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  const v = useMemo(() => ({ openPalette }), [openPalette]);

  return (
    <Ctx.Provider value={v}>
      {children}
      <CommandPaletteModal open={open} onClose={closePalette} />
    </Ctx.Provider>
  );
}

export function useCommandPalette() {
  return useContext(Ctx);
}
